import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator, Euro, Info, CheckCircle, AlertTriangle } from "lucide-react";

/**
 * Modal de calcul personnalisé pour chaque aide
 * Pose les questions spécifiques à l'aide et calcule le montant
 */
export default function AidCalculatorModal({ 
  isOpen, 
  onClose, 
  program, 
  incomeCategory,
  onCalculate 
}) {
  const [formData, setFormData] = useState({
    devis_ht: "",
    surface_isolee: "",
    nombre_fenetres: "",
    dpe_avant: "",
    dpe_apres: "",
  });
  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setResult(null); // Reset result when input changes
  };

  const calculateAid = () => {
    const devisHT = parseInt(formData.devis_ht) || 0;
    const surface = parseInt(formData.surface_isolee) || 0;
    
    let amount = 0;
    let calculation = "";
    let ceiling = program.max_ceiling || 0;

    switch (program.id) {
      case "maprimeadapt":
        // MaPrimeAdapt' : taux selon catégorie de revenus
        // 70% pour revenus très modestes (bleu)
        // 50% pour revenus modestes (jaune, violet, rose)
        const mpaRate = incomeCategory === 'bleu' ? 70 : 50;
        const maxWorkCost = 22000;
        const eligibleCost = Math.min(devisHT, maxWorkCost);
        amount = Math.floor(eligibleCost * mpaRate / 100);
        ceiling = Math.floor(maxWorkCost * mpaRate / 100);
        calculation = `${mpaRate}% × min(${devisHT.toLocaleString('fr-FR')}€, ${maxWorkCost.toLocaleString('fr-FR')}€) = ${amount.toLocaleString('fr-FR')}€`;
        break;

      case "maprimerenov_geste":
        // MaPrimeRénov' par geste : montants forfaitaires selon catégorie
        // Simplifié : on utilise le plafond global par catégorie
        const mprCeilings = { bleu: 11000, jaune: 9000, violet: 6000, rose: 0 };
        ceiling = mprCeilings[incomeCategory] || 6000;
        amount = Math.min(devisHT * 0.5, ceiling); // Estimation 50% du devis
        calculation = `Plafond ${incomeCategory}: ${ceiling.toLocaleString('fr-FR')}€`;
        break;

      case "cee":
        // Prime CEE : dépend du type de travail et de la surface
        // Estimation simplifiée
        const ceePerSqm = incomeCategory === "bleu" || incomeCategory === "jaune" ? 25 : 15;
        if (surface > 0) {
          amount = surface * ceePerSqm;
          calculation = `${surface}m² × ${ceePerSqm}€/m² = ${amount.toLocaleString('fr-FR')}€`;
        } else {
          amount = Math.floor(devisHT * 0.15);
          calculation = `~15% du devis = ${amount.toLocaleString('fr-FR')}€`;
        }
        ceiling = 5000;
        amount = Math.min(amount, ceiling);
        break;

      case "tva_reduite":
        // TVA 5,5% : économie de 14,5% par rapport à 20%
        amount = Math.floor(devisHT * 0.145);
        calculation = `${devisHT.toLocaleString('fr-FR')}€ × 14,5% (différence TVA) = ${amount.toLocaleString('fr-FR')}€`;
        ceiling = null; // Pas de plafond
        break;

      case "eco_ptz":
        // Éco-PTZ : prêt à taux zéro, plafond selon nombre de gestes
        const ecoptzCeilings = { 1: 15000, 2: 25000, 3: 50000 };
        ceiling = ecoptzCeilings[2] || 25000; // Par défaut 2 gestes
        amount = Math.min(devisHT, ceiling);
        calculation = `Montant empruntable : jusqu'à ${ceiling.toLocaleString('fr-FR')}€ à taux 0%`;
        break;

      case "maprimerenov_copro":
      case "maprimerenov_accompagne":
        // Réno d'ampleur : 30-45% selon gain énergétique
        const renoRate = 35; // Estimation moyenne
        const renoMaxPerDwelling = 25000;
        amount = Math.floor(Math.min(devisHT, renoMaxPerDwelling) * renoRate / 100);
        ceiling = Math.floor(renoMaxPerDwelling * 0.45);
        calculation = `~${renoRate}% × min(${devisHT.toLocaleString('fr-FR')}€, ${renoMaxPerDwelling.toLocaleString('fr-FR')}€) = ${amount.toLocaleString('fr-FR')}€`;
        break;

      default:
        // Aide locale ou autre
        if (program.funding_rate) {
          amount = Math.floor(devisHT * program.funding_rate / 100);
          ceiling = program.max_ceiling;
          amount = ceiling ? Math.min(amount, ceiling) : amount;
          calculation = `${program.funding_rate}% × ${devisHT.toLocaleString('fr-FR')}€ = ${amount.toLocaleString('fr-FR')}€`;
        } else {
          amount = program.estimated_amount || 0;
          calculation = "Montant forfaitaire";
        }
    }

    setResult({
      amount,
      calculation,
      ceiling,
      devisHT
    });

    if (onCalculate) {
      onCalculate(program.id, amount);
    }
  };

  const getQuestionsForProgram = () => {
    switch (program?.id) {
      case "maprimeadapt":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="devis_ht">Montant du devis HT (€) *</Label>
              <Input
                id="devis_ht"
                name="devis_ht"
                type="number"
                value={formData.devis_ht}
                onChange={handleChange}
                placeholder="Ex: 15000"
                className="h-12"
              />
              <p className="text-xs text-slate-500">
                Devis pour les travaux d'adaptation (douche, monte-escalier, etc.)
              </p>
            </div>
          </div>
        );

      case "maprimerenov_geste":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="devis_ht">Montant total du devis HT (€) *</Label>
              <Input
                id="devis_ht"
                name="devis_ht"
                type="number"
                value={formData.devis_ht}
                onChange={handleChange}
                placeholder="Ex: 8000"
                className="h-12"
              />
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-blue-800 mb-1">💡 Parcours par geste</p>
              <p className="text-blue-700 text-xs">
                Montants forfaitaires selon le type de travaux. 
                Le calcul précis dépend de chaque geste (isolation, chauffage, etc.).
              </p>
            </div>
          </div>
        );

      case "cee":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="devis_ht">Montant du devis HT (€)</Label>
              <Input
                id="devis_ht"
                name="devis_ht"
                type="number"
                value={formData.devis_ht}
                onChange={handleChange}
                placeholder="Ex: 10000"
                className="h-12"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="surface_isolee">Surface isolée (m²) - si applicable</Label>
              <Input
                id="surface_isolee"
                name="surface_isolee"
                type="number"
                value={formData.surface_isolee}
                onChange={handleChange}
                placeholder="Ex: 100"
                className="h-12"
              />
            </div>
          </div>
        );

      case "tva_reduite":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="devis_ht">Montant du devis HT (€) *</Label>
              <Input
                id="devis_ht"
                name="devis_ht"
                type="number"
                value={formData.devis_ht}
                onChange={handleChange}
                placeholder="Ex: 20000"
                className="h-12"
              />
            </div>
            <div className="bg-green-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-green-800 mb-1">✓ Économie automatique</p>
              <p className="text-green-700 text-xs">
                La TVA réduite à 5,5% au lieu de 20% est appliquée directement sur votre facture.
              </p>
            </div>
          </div>
        );

      case "eco_ptz":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="devis_ht">Montant total des travaux HT (€) *</Label>
              <Input
                id="devis_ht"
                name="devis_ht"
                type="number"
                value={formData.devis_ht}
                onChange={handleChange}
                placeholder="Ex: 25000"
                className="h-12"
              />
            </div>
            <div className="bg-purple-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-purple-800 mb-1">🏦 Prêt sans intérêts</p>
              <p className="text-purple-700 text-xs">
                L'Éco-PTZ finance le reste à charge après aides. 
                Plafond: 15 000€ (1 geste), 25 000€ (2 gestes), 50 000€ (3+ gestes).
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="devis_ht">Montant du devis HT (€)</Label>
              <Input
                id="devis_ht"
                name="devis_ht"
                type="number"
                value={formData.devis_ht}
                onChange={handleChange}
                placeholder="Ex: 10000"
                className="h-12"
              />
            </div>
          </div>
        );
    }
  };

  if (!program) return null;

  // Pour MaPrimeAdapt', calculer le taux réel selon la catégorie de revenus
  const getDisplayRate = () => {
    if (program.id === 'maprimeadapt') {
      return incomeCategory === 'bleu' ? 70 : 50;
    }
    return program.funding_rate;
  };
  
  const displayRate = getDisplayRate();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]">
            <Calculator className="h-5 w-5" />
            Calculer mon aide
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info programme */}
          <div className="bg-slate-50 rounded-lg p-3">
            <h4 className="font-semibold text-sm">{program.name}</h4>
            <p className="text-xs text-slate-600 mt-1">
              {displayRate && `${displayRate}% du devis HT`}
              {program.max_ceiling && ` • Plafond : ${program.max_ceiling.toLocaleString('fr-FR')}€`}
            </p>
            {program.valid_from && (
              <p className="text-xs text-slate-500 mt-1">
                Valide du {program.valid_from} au {program.valid_to || "indéfini"}
              </p>
            )}
          </div>

          {/* Questions spécifiques */}
          {getQuestionsForProgram()}

          {/* Bouton calculer */}
          <Button 
            onClick={calculateAid} 
            className="w-full h-12 bg-[#1A4D2E] hover:bg-[#143d24]"
            disabled={!formData.devis_ht}
          >
            <Calculator className="h-4 w-4 mr-2" />
            Calculer le montant
          </Button>

          {/* Résultat */}
          {result && (
            <div className="bg-gradient-to-br from-[#E8F5E9] to-[#f0f9f1] rounded-xl p-4 border border-[#1A4D2E]/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Montant estimé :</span>
                <span className="text-2xl font-bold text-[#1A4D2E]">
                  {result.amount.toLocaleString('fr-FR')} €
                </span>
              </div>
              <p className="text-xs text-slate-600 mb-2">{result.calculation}</p>
              {result.ceiling && (
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Plafond applicable : {result.ceiling.toLocaleString('fr-FR')} €
                </p>
              )}
              
              <div className="mt-3 pt-3 border-t border-[#1A4D2E]/10">
                <p className="text-xs text-amber-700 flex items-start gap-1">
                  <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
                  Estimation indicative. Le montant définitif sera confirmé lors de l'instruction de votre dossier.
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
